import React, { useEffect, useMemo, useState } from 'react';
import { ToggleLeft, ToggleRight, Save, RotateCcw, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import './OrganizationFeaturesTab.css';
import {
  getOrgFeatures,
  updateOrgFeatures,
  OrgFeature,
  FeatureUpdate,
} from '@/services/PagesServices/OrgFeaturesService';
import { getAllOrganizations, Organization } from '@/services/PagesServices/OrganizationService';

/**
 * Super Admin — Organization Features (tab of the Organizations page).
 * Pick an organization, then toggle which platform functionalities it is
 * allowed to use. OPT-OUT model: everything is ON by default; turning a
 * feature OFF hides its menu items for that org and blocks the matching API.
 */
const OrganizationFeaturesTab: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

  const [features, setFeatures] = useState<OrgFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [saving, setSaving] = useState(false);

  // feature_key -> enabled (the working copy the toggles mutate)
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  // Load organizations once for the picker. This deliberately fetches its own list
  // rather than reading `organizations` off useOrganizationStore: that store's list is
  // paginated and re-filtered by the Manage Organizations tab's search box, so sharing
  // it would silently narrow this picker to whatever was last searched for.
  useEffect(() => {
    let active = true;
    setLoadingOrgs(true);
    getAllOrganizations({ isActive: true, limit: 200 }).then((res) => {
      if (!active) return;
      setLoadingOrgs(false);
      if (res.success && res.data) {
        setOrganizations(res.data);
        if (res.data.length && selectedOrgId === null) setSelectedOrgId(res.data[0].id);
      } else {
        toast.error(res.error?.message || 'Failed to load organizations');
      }
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the chosen org's feature map whenever the selection changes.
  useEffect(() => {
    if (selectedOrgId === null) return;
    let active = true;
    setLoadingFeatures(true);
    getOrgFeatures(selectedOrgId).then((res) => {
      if (!active) return;
      setLoadingFeatures(false);
      if (res.success && res.data) {
        setFeatures(res.data);
        const d: Record<string, boolean> = {};
        res.data.forEach((f) => { d[f.key] = f.enabled; });
        setDraft(d);
      } else {
        toast.error(res.error?.message || 'Failed to load organization features');
      }
    });
    return () => { active = false; };
  }, [selectedOrgId]);

  // Group features by module for display.
  const grouped = useMemo(() => {
    const map = new Map<string, OrgFeature[]>();
    features.forEach((f) => {
      if (!map.has(f.module)) map.set(f.module, []);
      map.get(f.module)!.push(f);
    });
    return Array.from(map.entries());
  }, [features]);

  // What changed vs. the loaded state.
  const changes: FeatureUpdate[] = useMemo(
    () => features
      .filter((f) => draft[f.key] !== f.enabled)
      .map((f) => ({ key: f.key, enabled: draft[f.key] })),
    [features, draft]
  );
  const dirty = changes.length > 0;

  const enabledCount = useMemo(() => Object.values(draft).filter(Boolean).length, [draft]);

  const toggle = (key: string) => setDraft((d) => ({ ...d, [key]: !d[key] }));

  const setAll = (enabled: boolean) => {
    const d: Record<string, boolean> = {};
    features.forEach((f) => { d[f.key] = enabled; });
    setDraft(d);
  };

  const reset = () => {
    const d: Record<string, boolean> = {};
    features.forEach((f) => { d[f.key] = f.enabled; });
    setDraft(d);
  };

  const save = async () => {
    if (selectedOrgId === null || !dirty) return;
    setSaving(true);
    const res = await updateOrgFeatures(selectedOrgId, changes);
    setSaving(false);
    if (res.success) {
      toast.success(res.message || 'Organization features updated');
      // Reflect the saved state as the new baseline.
      setFeatures((prev) => prev.map((f) => ({ ...f, enabled: draft[f.key] })));
    } else {
      toast.error(res.error?.message || 'Failed to update features');
    }
  };

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId) || null;

  return (
    <div className="s-orgfeat">
      <div className="s-orgfeat-toolbar">
        <label className="s-orgfeat-picker">
          <Building2 size={16} />
          <select
            value={selectedOrgId ?? ''}
            onChange={(e) => setSelectedOrgId(e.target.value ? Number(e.target.value) : null)}
            disabled={loadingOrgs || organizations.length === 0}
          >
            {loadingOrgs && <option>Loading organizations…</option>}
            {!loadingOrgs && organizations.length === 0 && <option>No organizations</option>}
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>

        <div className="s-orgfeat-toolbar-right">
          <span className="s-orgfeat-muted">{enabledCount}/{features.length} enabled</span>
          <button className="s-orgfeat-btn-ghost" onClick={() => setAll(true)} disabled={!features.length}>Enable all</button>
          <button className="s-orgfeat-btn-ghost" onClick={() => setAll(false)} disabled={!features.length}>Disable all</button>
          <button className="s-orgfeat-btn-ghost" onClick={reset} disabled={!dirty}><RotateCcw size={14} /> Reset</button>
          <button className="s-orgfeat-btn-primary" onClick={save} disabled={!dirty || saving}>
            <Save size={16} /> {saving ? 'Saving…' : `Save${dirty ? ` (${changes.length})` : ''}`}
          </button>
        </div>
      </div>

      {selectedOrg && (
        <div className="s-orgfeat-note">
          Everything is enabled by default — turn a feature off to hide it from this org's menus and
          block its API. Editing functionalities for <strong>{selectedOrg.name}</strong>. Changes
          apply to every user in this org.
        </div>
      )}

      {loadingFeatures ? (
        <div className="s-orgfeat-empty">Loading features…</div>
      ) : (
        <div className="s-orgfeat-modules">
          {grouped.map(([module, items]) => (
            <section key={module} className="s-orgfeat-module">
              <h2 className="s-orgfeat-module-title">{module}</h2>
              <div className="s-orgfeat-grid">
                {items.map((f) => {
                  const on = !!draft[f.key];
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className={`s-orgfeat-card ${on ? 'on' : 'off'}`}
                      onClick={() => toggle(f.key)}
                      aria-pressed={on}
                    >
                      <div className="s-orgfeat-card-head">
                        <span className="s-orgfeat-card-label">{f.label}</span>
                        {on
                          ? <ToggleRight size={22} className="s-orgfeat-toggle on" />
                          : <ToggleLeft size={22} className="s-orgfeat-toggle off" />}
                      </div>
                      <p className="s-orgfeat-card-desc">{f.description}</p>
                      <code className="s-orgfeat-card-key">{f.key}</code>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationFeaturesTab;
